import { useNavigate } from "react-router-dom";
import { useShare } from "./ShareFlow";
import { JOURNEY_TYPES, LANGUAGES } from "@/lib/api";

const ORGANS = [
  { slug: "kidney", name: "Kidney" }, { slug: "liver", name: "Liver" }, { slug: "heart", name: "Heart" },
  { slug: "lung", name: "Lung" }, { slug: "pancreas", name: "Pancreas" }, { slug: "intestine", name: "Intestine" }, { slug: "other", name: "Other" },
];
const years = Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i);

export function Step({ title, children, testId }) {
  return (
    <div data-testid={testId} className="mtj-rise">
      <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-ink mb-8">{title}</h1>
      {children}
    </div>
  );
}

function Options({ value, onChange, options, name }) {
  return (
    <div className="space-y-3">
      {options.map((o) => (
        <button key={o.value} data-testid={`${name}-${o.value}`} onClick={() => onChange(o.value)}
          className={`w-full text-left px-5 py-4 border transition-colors ${value === o.value ? "border-ink bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`}>
          <span className={`text-base ${value === o.value ? "text-ink font-medium" : "text-neutral-700"}`}>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export function IntroStep() {
  const navigate = useNavigate();
  return (
    <div data-testid="step-0" className="mtj-rise py-10 text-center">
      <div className="text-[11px] font-mono uppercase tracking-widest text-rust mb-6">Before you begin</div>
      <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-ink">Your story matters.</h1>
      <div className="mt-10 max-w-lg mx-auto space-y-5 text-base sm:text-lg text-neutral-600 leading-relaxed text-left">
        <p>You're about to share an experience that may help someone else on their transplant journey.</p>
        <p className="text-ink font-medium">Be honest. Be authentic. Be respectful.</p>
        <p>Share what really happened — the good, the difficult, and what you wish you had known.</p>
        <p>Your experience could change someone else's journey.</p>
      </div>
      <button data-testid="share-begin" onClick={() => navigate("/share/about")}
        className="mt-12 bg-rust text-white px-8 py-4 text-sm font-medium hover:bg-rust-hover transition-colors">
        Begin Your Journey →
      </button>
      <p className="mt-5 text-xs font-mono text-neutral-400">Takes about 3 minutes · Anonymous by default</p>
    </div>
  );
}

export function AboutStep() {
  const { form, set } = useShare();
  return (
    <Step title="About your journey" testId="step-1">
      <label className="block text-sm font-mono text-neutral-500 mb-3">I am a:</label>
      <Options value={form.journey_type} onChange={(v) => set("journey_type", v)} options={JOURNEY_TYPES} name="type" />
      <label className="block text-sm font-mono text-neutral-500 mb-3 mt-10">My journey involved:</label>
      <Options value={form.organ} onChange={(v) => set("organ", v)} name="organ"
        options={ORGANS.map((o) => ({ value: o.slug, label: o.name }))} />
    </Step>
  );
}

export function WhereStep() {
  const { form, set, states, cities, centres, organStates } = useShare();
  return (
    <Step title="Where did this happen?" testId="step-2">
      <label className="block text-sm font-mono text-neutral-500 mb-2">State / UT</label>
      <select data-testid="share-state" value={form.state_code} onChange={(e) => { set("state_code", e.target.value); set("city", ""); set("hospital_id", ""); set("hospital_name_text", ""); }}
        className="w-full border border-neutral-300 px-4 py-3 bg-white outline-none focus:border-rust transition-colors">
        <option value="">Select state</option>
        {states.filter((s) => !organStates || organStates.has(s.code)).map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
      </select>
      {organStates && (
        <p className="text-xs text-neutral-400 mt-2">Showing states with a listed {form.organ} transplant center.</p>
      )}
      {cities.length > 0 && (
        <>
          <label className="block text-sm font-mono text-neutral-500 mb-2 mt-5">City (optional)</label>
          <select data-testid="share-city" value={form.city} onChange={(e) => set("city", e.target.value)}
            className="w-full border border-neutral-300 px-4 py-3 bg-white outline-none focus:border-rust transition-colors">
            <option value="">Select city</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </>
      )}
      {form.state_code && (
        <>
          <label className="block text-sm font-mono text-neutral-500 mb-2 mt-6">Hospital <span className="text-rust">*</span></label>
          <select data-testid="share-hospital" value={form.hospital_id} onChange={(e) => { set("hospital_id", e.target.value); if (e.target.value !== "__not_listed") set("hospital_name_text", ""); }}
            className="w-full border border-neutral-300 px-4 py-3 bg-white outline-none focus:border-rust transition-colors">
            <option value="">Select hospital</option>
            {centres.map((c) => <option key={c.id} value={c.id}>{c.hospital_name} — {c.city}</option>)}
            <option value="__not_listed">My hospital is not listed here — I'll type it</option>
          </select>
          {form.hospital_id === "__not_listed" && (
            <input
              data-testid="share-hospital-text"
              value={form.hospital_name_text}
              onChange={(e) => set("hospital_name_text", e.target.value)}
              placeholder="Type the hospital name"
              className="w-full border border-neutral-300 px-4 py-3 bg-white outline-none focus:border-rust transition-colors mt-3"
            />
          )}
          <p className="text-xs text-neutral-400 mt-3">
            {centres.length > 0
              ? `${centres.length} listed center${centres.length === 1 ? "" : "s"} offering this transplant in this state.`
              : "No listed centers offering this transplant in this state yet — choose \u201CMy hospital is not listed here\u201D and type the name."}
          </p>
        </>
      )}
    </Step>
  );
}

export function DetailsStep() {
  const { form, set } = useShare();
  return (
    <Step title="Year & language" testId="step-3">
      <label className="block text-sm font-mono text-neutral-500 mb-2">Which year was this?</label>
      <select data-testid="share-year" value={form.journey_year} onChange={(e) => set("journey_year", e.target.value)}
        className="w-full border border-neutral-300 px-4 py-3 bg-white outline-none focus:border-rust transition-colors">
        <option value="">Select year</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <label className="block text-sm font-mono text-neutral-500 mb-2 mt-6">Which language will you share your story in?</label>
      <div className="flex flex-wrap gap-2">
        {LANGUAGES.map((l) => (
          <button key={l} data-testid={`share-lang-${l}`} onClick={() => set("language", l)}
            className={`px-4 py-2 text-sm rounded-full border transition-colors ${form.language === l ? "bg-ink text-white border-ink" : "border-neutral-300 hover:border-ink"}`}>
            {l}
          </button>
        ))}
      </div>
    </Step>
  );
}

export function ThanksStep() {
  const navigate = useNavigate();
  return (
    <div data-testid="step-thanks" className="mtj-rise py-10 text-center">
      <div className="text-[11px] font-mono uppercase tracking-widest text-rust mb-6">Received</div>
      <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-ink">Thank you for sharing your journey.</h1>
      <div className="mt-10 max-w-lg mx-auto space-y-5 text-base sm:text-lg text-neutral-600 leading-relaxed text-left">
        <p>Your story is now <span className="text-ink font-medium">under review</span> by our team.</p>
        <p>Once approved, it will appear on Transplant Story — and it may be exactly what another family needs to hear.</p>
      </div>
      <div className="mt-12 flex items-center justify-center gap-4">
        <button data-testid="thanks-explore" onClick={() => navigate("/explore")}
          className="bg-ink text-white px-7 py-3 text-sm font-medium hover:bg-rust transition-colors">
          Explore journeys
        </button>
        <button data-testid="thanks-home" onClick={() => navigate("/")}
          className="text-sm text-neutral-500 hover:text-ink transition-colors">
          Back to home
        </button>
      </div>
    </div>
  );
}
